/* eslint-disable no-restricted-globals */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import locationIcon from '@assets/theme/images/pin.png';
import { MAP_ZOOM } from '@location/constants';
import MapPositionModel, {
  ICON,
  ICON_SIZE,
  LATITUDE,
  LONGITUDE,
  ICON_SIZE_X,
  ICON_SIZE_Y,
  ID,
} from './models/MapPosition';
import MapModel from './models/Map';
import Container from './components/Container';

function isValidLocation(location) {
  return (
    location &&
    !isNaN(location.latitude) &&
    !isNaN(location.longitude) &&
    location.latitude !== null &&
    location.longitude !== null
  );
}

export class GoogleMap extends React.PureComponent {
  mapRef = React.createRef();

  state = { zoomChangedManually: false };

  componentDidMount() {
    if (!this.map) {
      this.initMap();
    }
  }

  componentDidUpdate(prevProps) {
    const { markers } = this.props;
    if (this.map && markers !== prevProps.markers) {
      this.setMapMarkers();
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.unmount();
    }
  }

  saveLocation = location => {
    const { onMapClick } = this.props;
    if (isValidLocation(location)) {
      onMapClick(location);
    }
  };

  createMarker({
    [ID]: id,
    [LATITUDE]: latitude,
    [LONGITUDE]: longitude,
    [ICON]: iconUrl,
    [ICON_SIZE]: iconSize,
  }) {
    return this.map.addMarker({
      id,
      position: { latitude, longitude },
      iconUrl: iconUrl || locationIcon,
      iconSize,
      onDragEnd: this.onDragEnd,
    });
  }

  setMapMarker(marker) {
    const { shouldCenter, center } = this.props;
    const { zoomChangedManually } = this.state;
    if (isValidLocation(marker)) {
      const mapMarker = this.createMarker(marker);
      if (shouldCenter) {
        this.map.setCenter(marker);
      }
      if (!zoomChangedManually) {
        this.setLocationZoom();
      }
      return mapMarker;
    }
    this.map.setCenter(center);
    return null;
  }

  setMapMarkers() {
    const { markers, center } = this.props;
    markers.forEach(marker => {
      this.setMapMarker(marker);
    });

    if (!center) this.map.setCenter();
  }

  getInitialMapProps() {
    const { zoom, center, markers, isEditable } = this.props;
    const mapProps = { zoom, center, isEditable };
    if (
      (markers.length === 1 || (markers.length > 1 && !center)) &&
      isValidLocation(markers[0])
    ) {
      [mapProps.center] = markers;
    }

    return mapProps;
  }

  initMap() {
    const mapProps = this.getInitialMapProps();
    this.map = new MapModel(this.mapRef.current, mapProps);
    this.map.onVisible(this.onVisible);
    this.map.onZoomChanged(this.onZoomChangedManually);
  }

  onVisible = () => {
    this.setMapMarkers();
    this.map.onClick(this.onClick);
  };

  onClick = ({ latitude, longitude }) => {
    const position = { latitude, longitude };
    this.saveLocation(position);

    const marker = this.setMapMarker(MapPositionModel(position));
    this.setCenterIfZoomChangedManually(marker?.getPosition());
  };

  onDragEnd = ({ latitude, longitude }) => {
    const position = { latitude, longitude };
    this.saveLocation(position);
    this.setCenterIfZoomChangedManually(position);
  };

  onZoomChangedManually = () => {
    this.setState({ zoomChangedManually: true });
  };

  setLocationZoom = () => {
    this.map.setZoom(MAP_ZOOM.LOCATION);
    this.setState({ zoomChangedManually: false });
  };

  setCenterIfZoomChangedManually = center => {
    const { zoomChangedManually } = this.state;
    if (center && zoomChangedManually) {
      this.map.setCenter(center);
    }
    this.setLocationZoom();
  };

  render() {
    const { className, height } = this.props;
    return (
      <Container>
        <div
          style={{ height }}
          ref={this.mapRef}
          id="map"
          className={classnames(className)}
        />
      </Container>
    );
  }
}

GoogleMap.propTypes = {
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      [ID]: PropTypes.number.isRequired,
      [LATITUDE]: PropTypes.number.isRequired,
      [LONGITUDE]: PropTypes.number.isRequired,
      [ICON]: PropTypes.string,
      [ICON_SIZE]: PropTypes.shape({
        [ICON_SIZE_X]: PropTypes.number.isRequired,
        [ICON_SIZE_Y]: PropTypes.number.isRequired,
      }),
    }),
  ).isRequired,
  center: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
  className: PropTypes.string,
  isEditable: PropTypes.bool,
  shouldCenter: PropTypes.bool,
  zoom: PropTypes.number,
  height: PropTypes.string,
  onMapClick: PropTypes.func,
};

GoogleMap.defaultProps = {
  center: null,
  isEditable: true,
  className: '',
  zoom: MAP_ZOOM.LOCATION,
  height: '100%',
  shouldCenter: false,
};

export default GoogleMap;
